/**
 * Helpers for WhatsApp deep links: admin can store a full URL (wa.me, api.whatsapp.com, etc.)
 * or digits with country code (international E.164, no + sign).
 */

/** Add https:// when user pastes wa.me/... or api.whatsapp.com/... without a scheme */
export function normalizeWhatsAppLinkInput(input: string): string {
  const t = (input || '').trim()
  if (!t) return t
  if (/^https?:\/\//i.test(t)) return t
  const lower = t.toLowerCase()
  if (lower.startsWith('wa.me/') || lower.startsWith('www.wa.me/')) {
    return `https://${t.replace(/^\/+/, '')}`
  }
  if (
    lower.startsWith('api.whatsapp.com') ||
    lower.startsWith('www.api.whatsapp.com') ||
    lower.startsWith('chat.whatsapp.com/') ||
    lower.startsWith('www.chat.whatsapp.com/')
  ) {
    return `https://${t.replace(/^\/+/, '')}`
  }
  return t
}

/** True if this should be treated as a stored link (https URL), not legacy digits */
export function isWhatsAppHttpUrl(value: string): boolean {
  const n = normalizeWhatsAppLinkInput((value || '').trim())
  return /^https?:\/\//i.test(n)
}

/**
 * Normalize to WhatsApp `phone=` digits: country code + number, no + or spaces.
 * Supports international numbers — does NOT assume India (no auto 91 prefix).
 * Examples: +44 7911 123456 → 447911123456, 001 555 123 4567 → 15551234567
 */
export function normalizeWhatsAppPhoneDigits(value: string): string {
  let digits = (value || '').replace(/\D/g, '')
  if (digits.startsWith('00')) {
    digits = digits.slice(2)
  }
  return digits
}

/** @deprecated use normalizeWhatsAppPhoneDigits */
export function normalizePhoneDigits(value: string): string {
  return normalizeWhatsAppPhoneDigits(value)
}

/** E.164 length check (8–15 digits per ITU; includes country code) */
export function isValidWhatsAppPhoneDigits(value: string): boolean {
  const digits = normalizeWhatsAppPhoneDigits(value)
  return digits.length >= 8 && digits.length <= 15
}

export function buildWhatsAppUrlFromDigits(digitsOrE164: string): string {
  const phone = normalizeWhatsAppPhoneDigits(digitsOrE164)
  if (!phone) return 'https://api.whatsapp.com/send?phone='
  return `https://api.whatsapp.com/send?phone=${phone}`
}

/**
 * Merge optional pre-filled message into an existing WhatsApp URL (sets/overrides `text` query param).
 */
export function appendPrefillToWhatsAppUrl(baseUrl: string, prefillText?: string): string {
  const base = (baseUrl || '').trim()
  if (!prefillText) return base
  try {
    const url = new URL(base)
    url.searchParams.set('text', prefillText)
    return url.toString()
  } catch {
    const sep = base.includes('?') ? '&' : '?'
    return `${base}${sep}text=${encodeURIComponent(prefillText)}`
  }
}

/**
 * Resolve stored setting: full URL as-is (after normalizing scheme), or digits → send URL.
 */
export function resolveWhatsAppHref(stored: string, prefillText?: string): string {
  const raw = (stored || '').trim()
  if (!raw) return 'https://api.whatsapp.com/send?phone='
  const normalized = normalizeWhatsAppLinkInput(raw)
  if (/^https?:\/\//i.test(normalized)) {
    return appendPrefillToWhatsAppUrl(normalized, prefillText)
  }
  return appendPrefillToWhatsAppUrl(buildWhatsAppUrlFromDigits(raw), prefillText)
}
